import React, { useEffect, useState } from "react";
import { useList } from "react-firebase-hooks/database";
import { ref } from "firebase/database";
import { database } from "../../firebase/firebase";
import iconLinkedIn from "../../assets/linkedin.svg";
import iconInstagram from "../../assets/instagram.svg";
import iconTelegram from "../../assets/telegram.svg";

interface Post {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  readTime: number;
  publishedDate: string | null;
  author: string;
  authorImage: string;
  redirectLink: string;
}

const NewsletterPage: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [snapshots, loading, error] = useList(
    ref(database, "newsletter/posts")
  );

  useEffect(() => {
    if (snapshots) {
      const postsData: Post[] = snapshots.map((snapshot) => ({
        id: snapshot.key as string,
        ...(snapshot.val() as Omit<Post, "id">),
      }));
      setPosts(postsData);
    }
  }, [snapshots]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row">
        <div className="md:w-2/3 md:pr-8 md:border-r md:border-gray-300">
          <h1 className="text-4xl font-extrabold mb-8 text-[#FFAA33]">
            Lighthouse
          </h1>
          {posts.map((post, index) => (
            <div key={post.id}>
              <a
                href={post.redirectLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-8"
              >
                <div className="flex flex-col md:flex-row items-center">
                  <div className="w-full flex justify-center items-center md:w-1/3">
                    <img
                      src={post.imageUrl}
                      alt={post.title}
                      className="w-52 h-48 md:w-40 md:h-32 rounded-lg"
                    />
                  </div>
                  <div className="w-full md:w-2/3 mt-4 md:mt-0 md:pl-8">
                    <h2 className="text-2xl font-bold mb-2 hover:text-[#FF5C1F] transition duration-300 text-black text-opacity-80">
                      {post.title}
                    </h2>
                    <p className="text-black font-med mb-5 text-opacity-80">
                      {post.description}
                    </p>
                    <div className="flex justify-between items-center text-gray-500 text-opacity-80">
                      <div className="flex items-center">
                        <span>{post.author}</span>
                        {post.publishedDate && (
                          <>
                            <span className="mx-2">·</span>
                            <span>{post.publishedDate}</span>
                          </>
                        )}
                      </div>
                      <div className="font-med text-gray-400 text-opacity-80">
                        <span>{post.readTime} min read</span>
                      </div>
                    </div>
                  </div>
                </div>
              </a>
              {index !== posts.length - 1 && (
                <hr className="border-gray-300 my-8" />
              )}
            </div>
          ))}
        </div>
        <div className="md:w-1/3 md:pl-8 mt-8 md:mt-0">
          <h2 className="text-2xl font-bold mb-4 text-[#FFAA33]">
            What is Lighthouse?
          </h2>
          <p className="text-gray-600 mb-4 text-opacity-90">
            Rippl.sg's newsletter, Lighthouse, spotlights individuals pursuing
            unconventional projects and paths. Its aim is to encourage youths in
            Singapore to make a meaningful impact in society through their own
            unique ways, and to show that there are many paths to success in
            this world.
          </p>
          <div className="flex space-x-4">
            <a
              href="https://sg.linkedin.com/company/venka-media"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img className="h-6" src={iconLinkedIn} alt="LinkedIn" />
            </a>
            <a
              href="https://www.instagram.com/venkamedia/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img className="h-6" src={iconInstagram} alt="Instagram" />
            </a>
            <a href="https://t.me/venkamedia">
              <img className="h-6" src={iconTelegram} alt="Telegram" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPage;
